import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ArrowRight, GraduationCap, Sparkles } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { levelPathFor } from '../config/landings';

// Etiquetas de las tarjetas por idioma (los datos de nivel son los mismos).
const LEVEL_LABELS = {
  es: {
    'espanol-a1': { label: 'Principiante', tagline: 'Empieza desde cero' },
    'espanol-a2': { label: 'Elemental', tagline: 'Consolida tus bases' },
    'espanol-b1': { label: 'Intermedio', tagline: 'Gana fluidez' },
    'espanol-b2': { label: 'Intermedio alto', tagline: 'Comunica con precisión' },
    'espanol-c1-c2': { label: 'Avanzado', tagline: 'Domina los matices' },
  },
  de: {
    'espanol-a1': { label: 'Anfänger', tagline: 'Start bei Null' },
    'espanol-a2': { label: 'Grundkenntnisse', tagline: 'Basis festigen' },
    'espanol-b1': { label: 'Mittelstufe', tagline: 'Flüssiger sprechen' },
    'espanol-b2': { label: 'Obere Mittelstufe', tagline: 'Präzise kommunizieren' },
    'espanol-c1-c2': { label: 'Fortgeschritten', tagline: 'Nuancen meistern' },
  },
  en: {
    'espanol-a1': { label: 'Beginner', tagline: 'Start from scratch' },
    'espanol-a2': { label: 'Elementary', tagline: 'Build on the basics' },
    'espanol-b1': { label: 'Intermediate', tagline: 'Gain fluency' },
    'espanol-b2': { label: 'Upper intermediate', tagline: 'Communicate precisely' },
    'espanol-c1-c2': { label: 'Advanced', tagline: 'Master the nuances' },
  },
};

const L_COPY = {
  es: { kicker: 'Cursos por nivel MCER', title: 'Encuentra tu nivel de español',
        lead: 'Cinco itinerarios personalizados, del A1 al C2. Elige el tuyo o haz el test de nivel gratis si no lo tienes claro.',
        testKicker: 'Test gratuito', testTitle: '¿No sabes cuál es tu nivel?',
        testLead: '10 preguntas rápidas y sabrás si estás en A1, A2, B1, B2, C1 o C2.', testCta: 'Hacer test de nivel', cardCta: 'Ver el nivel' },
  de: { kicker: 'Kurse nach GER-Niveau', title: 'Finde dein Spanischniveau',
        lead: 'Fünf individuelle Lernwege von A1 bis C2. Wähle das Niveau, das zu dir passt – in der kostenlosen Probestunde schauen wir es gemeinsam an.',
        testKicker: 'Kostenloser Test', testTitle: 'Du kennst dein Niveau nicht?',
        testLead: '10 kurze Fragen und du weißt, ob du bei A1, A2, B1, B2, C1 oder C2 stehst.', testCta: 'Einstufungstest machen', cardCta: 'Spanisch online' },
  en: { kicker: 'Courses by CEFR level', title: 'Find your Spanish level',
        lead: 'Five personalised learning paths from A1 to C2. Pick the level that fits you — we confirm it together in the free trial lesson.',
        testKicker: 'Free test', testTitle: 'Not sure about your level?',
        testLead: '10 quick questions and you will know whether you are at A1, A2, B1, B2, C1 or C2.', testCta: 'Take the level test', cardCta: 'Spanish online' },
};

const LEVELS = [
  {
    slug: 'espanol-a1',
    code: 'A1',
    label: 'Principiante',
    anchor: 'Curso de Español A1 para principiantes',
    tagline: 'Empieza desde cero',
    color: '#A8D8EA',
  },
  {
    slug: 'espanol-a2',
    code: 'A2',
    label: 'Elemental',
    anchor: 'Curso de Español A2 (Nivel Elemental)',
    tagline: 'Consolida tus bases',
    color: '#F5A6A4',
  },
  {
    slug: 'espanol-b1',
    code: 'B1',
    label: 'Intermedio',
    anchor: 'Curso de Español B1: fluidez y confianza',
    tagline: 'Fluidez real al hablar',
    color: '#7DBDD9',
  },
  {
    slug: 'espanol-b2',
    code: 'B2',
    label: 'Intermedio alto',
    anchor: 'Curso de Español B2 (Intermedio Alto)',
    tagline: 'Perfeccionamiento y expresión',
    color: '#F5A6A4',
  },
  {
    slug: 'espanol-c1-c2',
    code: 'C1-C2',
    label: 'Avanzado',
    anchor: 'Clases de Español Avanzado C1-C2',
    tagline: 'Habla como un nativo',
    color: '#4A4A4A',
  },
];

export const Levels = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const { language } = useLanguage();
  const copy = L_COPY[language] || L_COPY.es;
  return (
    <section
      id="niveles"
      ref={ref}
      className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white via-[#E8F5FA] to-white"
    >
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <span className="text-[#F5A6A4] font-semibold text-sm uppercase tracking-wider">
            {copy.kicker}
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-[#4A4A4A] mt-4 mb-4">
            {copy.title}
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {copy.lead}
          </p>
        </motion.div>

        {/* Level cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5 mb-10">
          {LEVELS.map((lvl, i) => (
            <motion.div
              key={lvl.slug}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.4, delay: i * 0.06 }}
              whileHover={{ y: -6 }}
              data-testid={`level-card-${lvl.slug}`}
              className="relative bg-white rounded-2xl border border-gray-100 p-6 shadow-sm hover:shadow-xl transition-shadow overflow-hidden group"
            >
              <div
                className="absolute top-0 left-0 right-0 h-1.5"
                style={{ background: lvl.color }}
              />
              <div
                className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4 text-2xl font-extrabold"
                style={{ background: `${lvl.color}22`, color: lvl.color === '#4A4A4A' ? '#2F2F2F' : lvl.color }}
              >
                {lvl.code}
              </div>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">
                {language === 'es' ? lvl.label : LEVEL_LABELS[language][lvl.slug].label}
              </p>
              <h3 className="text-base font-bold text-[#2F2F2F] mb-4 leading-snug">
                {language === 'es' ? lvl.tagline : LEVEL_LABELS[language][lvl.slug].tagline}
              </h3>
              <Link
                to={levelPathFor(lvl.slug, language)}
                data-testid={`level-card-link-${lvl.slug}`}
                title={language === 'es' ? lvl.anchor : `${copy.cardCta} ${lvl.code}`}
                className="inline-flex items-center gap-1 text-sm font-bold text-[#7DBDD9] hover:text-[#4A4A4A] transition-colors"
              >
                {language === 'es' ? lvl.anchor : `${copy.cardCta} ${lvl.code}`}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          ))}
        </div>

        {/* Test banner: solo en español, el test aún no está traducido */}
        {language === 'es' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="rounded-3xl p-8 md:p-10 bg-gradient-to-r from-[#7DBDD9] to-[#F5A6A4] shadow-xl text-white flex flex-col md:flex-row items-center justify-between gap-6"
        >
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-2xl bg-white/25 flex items-center justify-center flex-shrink-0">
              <GraduationCap className="w-7 h-7 text-white" />
            </div>
            <div>
              <p className="text-xs font-bold tracking-wider uppercase text-white/85 mb-1">
                <Sparkles className="inline w-3 h-3 mr-1 -mt-0.5" />
                {copy.testKicker}
              </p>
              <h3 className="text-2xl font-extrabold leading-tight">{copy.testTitle}</h3>
              <p className="text-white/90 text-sm mt-1">
                {copy.testLead}
              </p>
            </div>
          </div>
          <Link
            to="/test-de-nivel"
            data-testid="levels-test-cta"
            className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-white text-[#2F2F2F] font-bold shadow-lg hover:shadow-2xl transition-all hover:-translate-y-0.5 whitespace-nowrap"
          >
            {copy.testCta}
            <ArrowRight className="w-5 h-5" />
          </Link>
        </motion.div>
        )}
      </div>
    </section>
  );
};
